import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckCircle, FilePdf, LinkSimple, Camera, MagicWand } from "@phosphor-icons/react";

/**
 * PastInspectionsStrip — horizontal strip of completed inspections for an
 * address. Each card shows title, version, when, photo count + quick
 * actions (View PDF, Copy Public Link). Clicking the card opens the
 * Inspection Radar wizard on the Recommend tab.
 *
 * Props:
 *   address (required) — property address string
 *   unit    (optional) — if provided, we show only inspections tied to
 *                        that unit; otherwise every completed inspection
 *                        at the address.
 *   limit   (optional) — default 8. Only the most recent N are rendered
 *                        with a "See all" link.
 */
export default function PastInspectionsStrip({ address, unit, limit = 8 }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    if (!address) return;
    (async () => {
      try {
        const { data } = await api.get("/prop-inspections-by-address", {
          params: { address },
        });
        if (!alive) return;
        let list = (data?.inspections || []).filter(
          (i) => (i.status === "completed" || i.status === "complete") && i.share_token
        );
        if (unit) {
          // Best-effort filter: prefer explicit unit_id, but also match on
          // titles that were pre-tagged with "Unit X".
          list = list.filter((i) => {
            if (i.unit_id) return true; // caller may want any tied to a unit
            const t = (i.title || "").toLowerCase();
            return t.includes(`unit ${String(unit).toLowerCase()}`);
          });
        }
        setItems(list);
      } catch (e) {
        if (!alive) return;
        // 404 = no property doc yet → strip stays hidden, not an error
        if (e?.response?.status !== 404) setErr("Couldn't load past inspections");
        setItems([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [address, unit]);

  if (!items || items.length === 0) {
    return null;
  }

  const shown = items.slice(0, limit);
  const token = () => localStorage.getItem("access_token") || "";
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const openReport = (iid) => {
    window.open(
      `${base}/api/prop-inspections/${iid}/report.pdf?auth=${encodeURIComponent(token())}`,
      "_blank"
    );
  };
  const copyLink = async (shareToken) => {
    const url = `${window.location.origin}/public/inspection/${shareToken}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied");
    } catch {
      toast.error("Copy failed — long-press to grab manually");
    }
  };

  return (
    <div
      data-testid="past-inspections-strip"
      className="rounded-2xl bg-white p-4 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-3">
        <MagicWand size={16} weight="fill" className="text-[#00C4CC]" />
        <h3 className="text-lg font-black text-[#0F1F3A]">Previous inspections</h3>
        <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">
          {items.length}
        </span>
        {err && (
          <span className="ml-auto text-[10px] text-red-500 font-black uppercase tracking-widest">
            {err}
          </span>
        )}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {shown.map((i) => {
          const when = i.completed_at ? new Date(i.completed_at) : null;
          const whenLabel = when
            ? when.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "—";
          return (
            <div
              key={i.inspection_id}
              data-testid={`past-inspection-${i.inspection_id}`}
              className="snap-start flex-shrink-0 w-64 rounded-xl border-2 border-slate-100 hover:border-[#00C4CC] transition-colors bg-white overflow-hidden"
            >
              <Link
                to={`/admin/inspections/${i.inspection_id}`}
                className="block p-3 pb-2"
              >
                <div className="flex items-center gap-1.5">
                  <CheckCircle size={14} weight="fill" className="text-emerald-500 flex-shrink-0" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">
                    v{i.report_version || 1}
                  </span>
                  <span className="ml-auto text-[10px] font-medium text-[#8B93A7]">
                    {whenLabel}
                  </span>
                </div>
                <div className="mt-1.5 text-sm font-black text-[#0F1F3A] leading-snug line-clamp-2">
                  {i.title || "Inspection"}
                </div>
                <div className="mt-1 text-[11px] text-[#8B93A7] font-medium flex items-center gap-2">
                  <span className="inline-flex items-center gap-1">
                    <Camera size={11} weight="fill" /> {i.photos_count || 0}
                  </span>
                  {typeof (i.recommendations || []).length === "number" &&
                    (i.recommendations || []).length > 0 && (
                      <span>· {(i.recommendations || []).length} recs</span>
                    )}
                  {typeof (i.findings || []).length === "number" &&
                    (i.findings || []).length > 0 && (
                      <span>· {(i.findings || []).length} issues</span>
                    )}
                </div>
              </Link>
              <div className="border-t border-slate-100 flex">
                <button
                  onClick={() => openReport(i.inspection_id)}
                  data-testid={`past-inspection-pdf-${i.inspection_id}`}
                  className="flex-1 h-9 flex items-center justify-center gap-1 text-[10px] font-black uppercase tracking-widest text-[#0F1F3A] hover:bg-slate-50 transition-colors"
                >
                  <FilePdf size={12} weight="fill" /> PDF
                </button>
                <div className="w-px bg-slate-100" />
                <button
                  onClick={() => copyLink(i.share_token)}
                  data-testid={`past-inspection-link-${i.inspection_id}`}
                  className="flex-1 h-9 flex items-center justify-center gap-1 text-[10px] font-black uppercase tracking-widest text-[#0F1F3A] hover:bg-slate-50 transition-colors"
                >
                  <LinkSimple size={12} weight="bold" /> Link
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {items.length > limit && (
        <div className="mt-2 text-[10px] font-black uppercase tracking-widest text-[#8B93A7]">
          Showing {limit} of {items.length}
        </div>
      )}
    </div>
  );
}
